import { useMemo, useState } from "react";
import { CheckCheck, Eye, Pencil, Trash2, UserCheck } from "lucide-react";
import moment from "moment";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { getPatientName } from "components/Header/utils/getPatientName";
import { DisplayTaskModal } from "components/Modal/DisplayTaskModal/DisplayTaskModal";
import { DataTable } from "components/ui/DataTable";
import { Pill } from "components/ui/Pill";
import { PILL_COLORS } from "components/ui/Pill/pillColors";
import { useClaimTask } from "features/comunications/hooks/useClaimTask";
import { useCompleteTask } from "features/comunications/hooks/useCompleteTask";
import { useDeleteTask } from "features/comunications/hooks/useDeleteTask";
import { usePaginatedTasks } from "features/comunications/hooks/usePaginatedTasks";
import { canClaimTask, getTaskAssignmentLabel } from "features/comunications/utils/formatTaskAssignment";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";

const PRIORITY_LABELS = { 0: "High", 1: "Medium", 2: "Low" };
const PRIORITY_PILL = {
  0: PILL_COLORS.reactionYes,
  1: PILL_COLORS.statusWarning,
  2: PILL_COLORS.reactionNo,
};
const STATUS_PILL = {
  Open: PILL_COLORS.reactionUnknown,
  Held: PILL_COLORS.statusWarning,
  Cancelled: PILL_COLORS.reactionYes,
  Complete: PILL_COLORS.reactionNo,
};

export const TaskTable = ({ goToPatientDetails }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editTask, setEditTask] = useState(null);

  const { data, isLoading } = usePaginatedTasks({ search, page, rows });
  const { mutate: deleteTask } = useDeleteTask();
  const { mutate: completeTask } = useCompleteTask();
  const { mutate: claimTask } = useClaimTask();
  const { data: staffInfo } = useStaffInfo();

  const columns = useMemo(
    () => [
      {
        id: "patient",
        header: "Patient",
        width: "170px",
        render: (item) => getPatientName(item.patient) || "N/A",
        getTooltipText: (item) => getPatientName(item.patient) || "N/A",
        onClick: (item) =>
          item.patient?.id &&
          goToPatientDetails(item.patient.id, getPatientName(item.patient)),
        clickableWhen: (item) => !!item.patient?.id,
      },
      {
        id: "title",
        header: "Instruction",
        width: "350px",
        render: (item) => item.title || "-",
        getTooltipText: (item) => item.title || "-",
        onClick: (item) => setSelectedTask(item),
        clickableSubtle: true,
      },
      {
        id: "assignedTo",
        header: "Assigned To",
        width: "170px",
        render: (item) => getTaskAssignmentLabel(item),
        getTooltipText: (item) => getTaskAssignmentLabel(item),
      },
      {
        id: "priority",
        header: "Priority",
        width: "90px",
        render: (item) => {
          const label = PRIORITY_LABELS[item.priority];
          const colors = PRIORITY_PILL[item.priority];
          return label ? <Pill {...colors}>{label}</Pill> : "-";
        },
      },
      {
        id: "dueDate",
        header: "Due Date",
        width: "110px",
        render: (item) =>
          item.dueDate ? moment(item.dueDate).format("DD-MM-YYYY") : "N/A",
      },
      {
        id: "status",
        header: "Status",
        width: "minmax(100px, 1fr)",
        render: (item) => {
          const colors =
            STATUS_PILL[item.status] || PILL_COLORS.reactionUnknown;
          return item.status ? <Pill {...colors}>{item.status}</Pill> : "-";
        },
      },
    ],
    [goToPatientDetails]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Claim",
        icon: UserCheck,
        primary: true,
        visibleWhen: (item) => canClaimTask(item, staffInfo?.staffMember),
        onClick: (item) => claimTask(item.id),
      },
      {
        label: "Complete",
        icon: CheckCheck,
        primary: true,
        visibleWhen: (item) => item.status !== "Complete",
        onClick: (item) => completeTask(item.id),
      },
      {
        label: "View",
        icon: Eye,
        primary: true,
        onClick: (item) => setSelectedTask(item),
      },
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        onClick: (item) => setEditTask(item),
      },
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        onClick: (item) => deleteTask(item.id),
      },
    ],
    [completeTask, deleteTask, claimTask, staffInfo]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={data?.data || []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No tasks found"
        emptyText="You don't have any tasks at this time."
        search={{
          value: search,
          onChange: (v) => {
            setSearch(v);
            setPage(1);
          },
          placeholder: "Search",
        }}
        currentPage={page}
        onPageChange={setPage}
        totalCount={data?.totalRecords}
        rowsValue={rows}
        onRowsChange={(val) => {
          setRows(val);
          setPage(1);
        }}
        rowActions={rowActions}
      />
      {selectedTask && (
        <DisplayTaskModal
          taskId={selectedTask.id}
          task={selectedTask}
          isOpen={!!selectedTask}
          closeModal={() => setSelectedTask(null)}
        />
      )}
      <AddTaskModal
        task={editTask}
        modalTitle="Edit Task"
        buttonText="Edit Task"
        noButton
        isOpen={!!editTask}
        setIsOpen={(open) => {
          if (!open) setEditTask(null);
        }}
      />
    </>
  );
};
